import Squad from "@skrice.studio/mavia-common/build/models/main/Squad";
import { CheckCircleOutline as CheckCircleOutlineIcon, HighlightOff as HighlightOffIcon, NotInterested as NotInterestedIcon } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button, CircularProgress,
    Grid,
    MenuItem,
    Paper,
    Select, Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow, TextField,
    Typography
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import * as React from "react";
import ReactCountryFlag from "react-country-flag";
import { NavLink } from "react-router-dom";
import { Avatar } from "../../components/Avatar";
import { CoatOfArms } from "../../components/CoatOfArms";
import { LeagueShield } from "../../components/League";
import { Config } from "../../config/config";
import { CountryCodes } from "../../utils/countryCodes";
import { getGlobalVar, Helpers, numberWithCommas } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";
import { DefaultProps } from "../LayoutWrapper";
import { AllianceChatHistory } from "./chatHistory";

export const TabPanel = (props: any) => {
    const {children, value, index, ...other} = props;

    return (
        <Typography
            component="div"
            role="tabpanel"
            hidden={value !== index}
            id={`nav-tabpanel-${index}`}
            aria-labelledby={`nav-tab-${index}`}
            {...other}
        >
            {value === index && <Box p={3}>{children}</Box>}
        </Typography>
    );
};

interface AllianceState {
    allianceId: string;
    squadData: Squad;
    dataLoaded: boolean;
    helpRequests: any[];

    original: {
        name: string;
        tag: string;
        gold_coins: number;
        tech_points: number;
        black_credits: number;
    };

    activeTab: string;
    roles: string[];
}

export class Alliance extends React.Component<DefaultProps, AllianceState> {
    constructor(props) {
        super(props);
        // List with grid items
        this.state = {
            allianceId: props.match.params.allianceId,
            squadData: {} as any,
            dataLoaded: false,
            helpRequests: [],

            original: {
                name: "",
                tag: "",
                gold_coins: 0,
                black_credits: 0,
                tech_points: 0,
            },

            activeTab: this.props.match.params.activeTab || "general",

            roles: [
                "Boss",
                "Adviser",
                "Commander",
                "Soldier",
                "Member",
                "Officer",
            ],
        };

        document.title = Helpers.documentTitle("Players");
    }

    componentDidMount() {
        this.fetchData();
    }

    fetchAllianceData = () => axios.get(`${Config.getInstance.apiUrl}/api/alliances/${this.state.allianceId}`, {
        headers: {Authorization: Helpers.getSessionId()},
    });

    fetchHelpRequests = () => axios.get(`${Config.getInstance.apiUrl}/api/alliances/${this.state.allianceId}/help_requests`, {
        headers: {Authorization: Helpers.getSessionId()},
    });

    fetchData = () =>
        axios.all([
            this.fetchAllianceData(),
            this.fetchHelpRequests(),
        ]).then(axios.spread((
            squadData, helpRequests
        ) => {
            this.setState({
                dataLoaded: true,
                squadData: squadData.data.data,
                helpRequests: helpRequests.data.data,
                original: {
                    name: squadData.data.data.name,
                    tag: squadData.data.data.tag,
                    gold_coins: squadData.data.data.gold_coins,
                    tech_points: squadData.data.data.tech_points,
                    black_credits: squadData.data.data.black_credits,
                },
            });

            document.title = Helpers.documentTitle(squadData.data.data.name);
        })).catch((_err) => {
            PopupMessage.error("Something went wrong");
        });

    resetInline = (key: string) => {
        const tempsquadData = {...this.state.squadData};
        tempsquadData[key] = this.state.original[key];

        this.setState({
            squadData: tempsquadData
        });
    };

    saveInline = (key: string) => {
        axios.put(`${Config.getInstance.apiUrl}/api/alliances/${this.state.allianceId}/${key}`, {
            value: this.state.squadData[key],
        }, {
            headers: {
                Authorization: Helpers.getSessionId(),
                env: this.props.activeEnv,
            },
        })
            .then(data =>
                this.setState(state => {
                    state.original[key] = state.squadData[key];

                    return state;
                }))
            .catch(() => {
                PopupMessage.error("Something went wrong!");
            });
    };

    changeInlineValue = (e, key) => {
        const tempSquadData = {...this.state.squadData};

        tempSquadData[key] = +e.target.value;

        if (isNaN(tempSquadData[key])) {
            tempSquadData[key] = e.target.value;
        }

        this.setState({
            squadData: tempSquadData
        });
    };

    onTabChange = (_event, tab: string) => {
        this.setState({activeTab: tab}, () => {
            Helpers.pushURL(`alliances/${this.state.allianceId}/${tab}`);
        });
    };

    changeMemberRole = async (e: any) => {
        const userId = e.target.name;
        const role = e.target.value;
        try {
            await axios.put(`${Config.getInstance.apiUrl}/api/user/${userId}/data`, {
                parameter: "squad_role",
                value: role,
            }, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                    env: this.props.activeEnv,
                },
            });

            this.fetchData();
            PopupMessage.success("User role changed!");
        } catch (e) {
            PopupMessage.error("Something went wrong!");
        }
    };
    renderMembersTab = () => {
        if (!this.state.dataLoaded) {
            return <div className="no-items-placeholder" style={{height: 150}}/>;
        }

        const items = this.state.squadData.members;

        if (items && items.length > 0) {
            return <div>
                <Table className="min-table-size">
                    <TableHead>
                        <TableRow>
                            <TableCell style={{width: 50}}></TableCell>
                            <TableCell style={{width: 120}}>Username</TableCell>
                            <TableCell style={{width: 130}}>Role</TableCell>
                            <TableCell>User id</TableCell>
                            <TableCell style={{width: 100}}>Joined</TableCell>
                            <TableCell style={{width: 50}}>League</TableCell>
                            <TableCell style={{width: 70}}>Gold</TableCell>
                            <TableCell style={{width: 180}}>Country</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {this.formatMembersTableBody(items)}
                    </TableBody>
                </Table>
            </div>;
        }

        return <div className="no-items-placeholder">
            <NotInterestedIcon/>
            <h3>No alliances found</h3>
        </div>;
    };

    formatMembersTableBody = (items: any[]) => {
        if (!items) {
            return [];
        }

        return items.sort((a, b) => a.squad_role - b.squad_role).map(item => (
            <TableRow
                key={item.id}
                hover={true}
            >
                <TableCell>{Avatar(item)}</TableCell>
                <TableCell>
                    <NavLink to={`/players/${item.id}`}>
                        {item.name}
                    </NavLink>
                </TableCell>
                <TableCell>
                    <Select
                        name={item.id}
                        value={item.squad_role}
                        onChange={this.changeMemberRole}
                        size="small"
                    >
                        {this.renderDropdown(this.state.roles)}
                    </Select>
                </TableCell>
                <TableCell>{item.id}</TableCell>
                <TableCell>{moment(item.squad_join_date).format("DD.MM.YYYY")}</TableCell>
                <TableCell align="center" style={{width: 50}}>
                    {LeagueShield({
						rank: item.rank,
						showRank: true,
						showLeagueName: true,
					})}
                </TableCell>
                <TableCell>{numberWithCommas(item.rubies)}</TableCell>
                <TableCell>
                    <ReactCountryFlag countryCode={item.country} svg={true}/>
                    <span style={{ marginLeft: 5, fontSize: 12 }}>
                        {CountryCodes.find(country => country.code === item.country)?.name || item.country}
                    </span>
                </TableCell>
            </TableRow>
        ));
    };

    renderDropdown = (values: string[]) => {
        const items = [];
        values.forEach((name, index) => {
            items.push(<MenuItem value={index}>{name}</MenuItem>);
        });
        return items;
    };

    renderPageHeader = () =>
        <Grid container>
            <Grid item xs={3}>
                <div className="player-username">
                    <h3>{this.state.squadData.name}</h3>
                </div>
            </Grid>
            <Grid item xs={9} className="header--buttons">

            </Grid>
        </Grid>;

    async resetBounties() {
        try {
            if (!confirm("Are you sure, you want to do this?")) {
                return;
            }

            await axios.post(`${Config.getInstance.apiUrl}/api/alliances/${this.state.allianceId}/reset-bounties`, {}, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                },
            });

            PopupMessage.success("Bounties successfully reset!");

            setTimeout(() => window.location.reload(), 3000);
        } catch (e) {
            PopupMessage.error("Something went wrong!");
        }
    };

    getBountyPackTitle(packId: string) {
        return getGlobalVar("BOUNTIES").find(item => item.id === packId)?.title || packId;
    }

    getBenefitConfig(benefitId: string) {
        return getGlobalVar("ALLIANCE_BENEFITS").find(item => item.id === benefitId);
    }

    render() {
        const formButtonStyle = {
            display: "inline-block",
            fontSize: 20,
            marginLeft: 5,
        };

        const successButtonStyle = {
            ...formButtonStyle,
            color: "green",
        };

        const cancelButtonStyle = {
            ...formButtonStyle,
        };

        const loadingContainer = <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translateX(-50%) translateY(-50%)",
        }}>
            <div className="loadingIndicator">
                <CircularProgress/>
                <span className="initText">Fetching alliance data</span>
            </div>
        </div>;

        const inlineInput = (key: string) => {
            if (this.state.squadData[key] !== this.state.original[key]) {
                return <div style={{position: "absolute", left: 215, top: 3, minWidth: 50}}>
                    <div style={successButtonStyle}>
                        <CheckCircleOutlineIcon onClick={() => this.saveInline(key)}/>
                    </div>
                    <div style={cancelButtonStyle}>
                        <HighlightOffIcon onClick={() => this.resetInline(key)}/>
                    </div>
                </div>;
            }

            return null;
        };

        return (
            <React.Fragment>
                {!this.state.dataLoaded && loadingContainer}

                {this.state.dataLoaded && <div className="player-page--wrapper" style={{marginLeft: 20}}>
                    <TabPanel value={this.state.activeTab} index="general">
                        <Grid container>
                            <Grid item xs={4}>
                                <Paper style={{ padding: 16 }} elevation={0}>
                                    <div className="ant-table-body">
                                        <table style={{width: "100%"}}>
                                            <tbody className="ant-table-tbody">

                                            <tr>
                                                <td colSpan={2} style={{textAlign: "center"}}>
                                                    <h2>{this.state.squadData.name}</h2>
                                                    <CoatOfArms emblem={this.state.squadData.flag} size="large"/>

                                                    <Typography variant="caption" display="block" gutterBottom style={{ textAlign: "left", marginBottom: 20 }}>
                                                        {this.state.squadData.description}
                                                    </Typography>
                                                </td>
                                            </tr>

                                            <tr>
                                                <td style={{width: "160px"}}><b>Alliance ID</b></td>
                                                <td>{this.state.squadData.id}</td>
                                            </tr>

                                            <tr>
                                                <td><b>Power</b></td>
                                                <td>{numberWithCommas(this.state.squadData.current_power)}</td>
                                            </tr>

                                            <tr>
                                                <td><b>Created</b></td>
                                                <td>{Helpers.formatDateWithTime(this.state.squadData.created_at)}</td>
                                            </tr>

                                            <tr>
                                                <td><b>Name</b></td>
                                                <td style={{position: "relative"}}>
                                                    <TextField
                                                        value={this.state.squadData.name}
                                                        onChange={(e) => this.changeInlineValue(e, "name")}
                                                        size="small"
                                                    />

                                                    {inlineInput("name")}
                                                </td>
                                            </tr>

                                            <tr style={{ padding: "10px 0" }}>
                                                <td><b>Tag</b></td>
                                                <td style={{position: "relative"}}>
                                                    <TextField
                                                        value={this.state.squadData.tag}
                                                        onChange={(e) => this.changeInlineValue(e, "tag")}
                                                        size="small"
                                                    />
                                                    {inlineInput("tag")}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td><b>Gold Coins</b></td>
                                                <td style={{position: "relative"}}>
                                                    <TextField
                                                        type="number"
                                                        value={this.state.squadData.gold_coins}
                                                        onChange={(e) => this.changeInlineValue(e, "gold_coins")}
                                                        size="small"
                                                    />

                                                    {inlineInput("gold_coins")}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td><b>Tech Points</b></td>
                                                <td style={{position: "relative"}}>
                                                    <TextField
                                                        type="number"
                                                        value={this.state.squadData.tech_points}
                                                        onChange={(e) => this.changeInlineValue(e, "tech_points")}
                                                        size="small"
                                                    />
                                                    {inlineInput("tech_points")}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td><b>Black Credits</b></td>
                                                <td style={{position: "relative"}}>
                                                    <TextField
                                                        type="number"
                                                        value={this.state.squadData.black_credits}
                                                        onChange={(e) => this.changeInlineValue(e, "black_credits")}
                                                        size="small"
                                                    />
                                                    {inlineInput("black_credits")}
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </Paper>

                                <Paper style={{ marginTop: 15, padding: 16 }} elevation={0}>
                                    <h3 style={{ marginTop: 0 }}>Bounties</h3>

                                    <Grid container spacing={2}>
                                        {this.state.squadData.bounties && Object.entries(this.state.squadData.bounties).map(entry => {
                                            const [key, bounty] = entry as any;

                                            return <Grid item sm={6} key={key}>
                                                <Paper style={{ padding: "10px 15px" }}>
                                                    <Typography variant="h6" style={{ marginBottom: 5 }}>
                                                        {Helpers.getLocalizationName(this.getBountyPackTitle(key))}
                                                    </Typography>

                                                    <tr>
                                                        <td style={{width: "160px"}}><b>Easy</b></td>
                                                        <td>{bounty.easyCurrent}/{bounty.easyMax}</td>
                                                    </tr>

                                                    <tr>
                                                        <td style={{width: "160px"}}><b>Medium</b></td>
                                                        <td>{bounty.mediumCurrent}/{bounty.mediumMax}</td>
                                                    </tr>

                                                    <tr>
                                                        <td style={{width: "160px"}}><b>Hard</b></td>
                                                        <td>{bounty.hardCurrent}/{bounty.hardMax}</td>
                                                    </tr>
                                                </Paper>
                                            </Grid>
                                        })}
                                    </Grid>
                                </Paper>

                                <Paper style={{ marginTop: 15, padding: 16 }} elevation={0}>
                                    <h3 style={{ marginTop: 0 }}>Tools</h3>

                                    <Button
                                        disableElevation
                                        variant="contained"
                                        style={{marginRight: 15}}
                                        onClick={() => this.resetBounties()}
                                    >
                                        Reset Bounties
                                    </Button>
                                </Paper>

                                <Paper style={{ marginTop: 15, padding: 16 }} elevation={0}>
                                    <h3 style={{ marginTop: 0 }}>Help Requests</h3>

                                    {this.state.helpRequests.length === 0 && <Alert severity="info">No Help Requests Found</Alert>}

                                    {this.state.helpRequests.length > 0 && <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Username</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Help count</TableCell>
                                                <TableCell>Time reduced</TableCell>
                                                <TableCell>Date</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {this.state.helpRequests.map(item =>
                                                <TableRow key={item.id}>
                                                    <TableCell>
                                                        <NavLink to={`/players/${item.user_id}`}>
                                                            {item.username}
                                                        </NavLink>
                                                    </TableCell>
                                                    <TableCell>{Helpers.capitalizeFirstLetter(item.request_type)}</TableCell>
                                                    <TableCell>{item.help_count}</TableCell>
                                                    <TableCell>{Helpers.fancyTimeFormat(item.help_time, {
                                                        hideSeconds: true
                                                    })}</TableCell>
                                                    <TableCell>{moment(item.help_request_time).format("DD.MM.YYYY HH:mm")}</TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>}
                                </Paper>

                                <Paper style={{ marginTop: 15, padding: 16 }} elevation={0}>
                                    <h3 style={{ marginTop: 0 }}>Gifts</h3>

                                    <div className="ant-table-body">
                                        <table style={{width: "100%"}}>
                                            <tbody className="ant-table-tbody">
                                                <tr>
                                                    <td><b>Gift level</b></td>
                                                    <td style={{position: "relative"}}>{this.state.squadData?.gift?.level || "-"}</td>
                                                </tr>
                                                <tr>
                                                    <td><b>Gift points</b></td>
                                                    <td style={{position: "relative"}}>{numberWithCommas(this.state.squadData?.gift?.points) || "-"}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </Paper>

                                <Paper style={{ marginTop: 15, padding: 16 }} elevation={0}>
                                    <h3 style={{ marginTop: 0 }}>Benefits</h3>

                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Id</TableCell>
                                                <TableCell>Name</TableCell>
                                                <TableCell>Level</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {this.state.squadData.benefits.map(item => {
                                                const benefitConfig = this.getBenefitConfig(item.id);
                                                const maxLevel = benefitConfig?.levels?.length || 0;

                                                return <TableRow key={item.id}>
                                                    <TableCell>{item.id}</TableCell>
                                                    <TableCell>
                                                        {Helpers.getLocalizationName(benefitConfig?.name)}
                                                        <p style={{ fontSize: 12, color: "#939393", margin: "3px 0 0 0" }}>
                                                            {Helpers.getLocalizationName(benefitConfig?.description)}
                                                        </p>
                                                    </TableCell>
                                                    <TableCell style={{ color: item.level === maxLevel ? "#81C784" : "#EF5350" }}>
                                                        {item.level} / {maxLevel}
                                                    </TableCell>
                                                </TableRow>
                                            })}
                                        </TableBody>
                                    </Table>
                                </Paper>

                                <Paper style={{ marginTop: 15, padding: 16 }} elevation={0}>
                                    <h3 style={{ marginTop: 0 }}>Chat history</h3>

                                    <AllianceChatHistory
                                        members={this.state.squadData.members}
                                        allianceId={this.state.allianceId}
                                        {...this.props}
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={8}>
                                <Paper style={{marginLeft: 20}} elevation={0}>
                                    {this.renderMembersTab()}
                                </Paper>
                            </Grid>
                        </Grid>
                    </TabPanel>
                </div>}
            </React.Fragment>
        );
    }
}
